import { z } from 'zod';

/**
 * One row of the "Setup de singularité" — replaces the old "6 leviers"
 * entirely (see docs/design/matrice-creation-wizard/2026-07-29-mss-implementation-design.md).
 * Fixed order 1-6 per company. `levierValeur` is what the product displays/
 * labels wherever it previously said "levier"; the other 7 fields feed the
 * generation prompt as context and are shown only on expand in the wizard's
 * setup-review step.
 *
 * Fields are nullable because they're filled in incrementally via company
 * config (not built this pass) — completeness is derived (see
 * `isSingularitySetupComplete`), not a stored flag.
 */
export const singularityLevierSchema = z.object({
  order: z.number().int().min(1).max(6),
  driver: z.string().max(500).nullable(),
  levierImplication: z.string().max(2_000).nullable(),
  detailImplication: z.string().max(2_000).nullable(),
  principeAction: z.string().max(2_000).nullable(),
  methode: z.string().max(2_000).nullable(),
  levierValeur: z.string().max(200).nullable(),
  detailValeur: z.string().max(2_000).nullable(),
  effetProduit: z.string().max(2_000).nullable(),
});
export type SingularityLevier = z.infer<typeof singularityLevierSchema>;

const BLOCKING_LEVIER_FIELDS = [
  'driver',
  'levierValeur',
] as const satisfies readonly (keyof SingularityLevier)[];

/**
 * `PUT /companies/:id/singularity-setup` — always exactly 6 rows (order
 * 1-6), updated in place. No insert/delete: a company's 6 setup rows are
 * seeded on creation and never added to or removed, only filled in.
 */
export const updateSingularitySetupInputSchema = z.array(singularityLevierSchema).length(6);
export type UpdateSingularitySetupInput = z.infer<typeof updateSingularitySetupInputSchema>;

/**
 * A company's setup is complete once all 6 leviers exist and each has its
 * `driver` and `levierValeur` filled in — the other 6 fields per row are
 * optional context and never block matrix creation.
 */
export function isSingularitySetupComplete(leviers: SingularityLevier[]): boolean {
  if (leviers.length !== 6) return false;
  return leviers.every((levier) =>
    BLOCKING_LEVIER_FIELDS.every((field) => {
      const value = levier[field];
      return typeof value === 'string' && value.trim().length > 0;
    }),
  );
}

/**
 * The name of each of the 6 setup steps, by `order - 1` (issue #16). The
 * wizard's setup list and the Cadrage tab title each row « Étape N - <nom> ».
 */
export const SINGULARITY_STEP_NAMES = [
  'Tension vitale',
  'Conditions',
  'Savoir-faire naturel',
  'Expérience',
  'Impact',
  'Sens profond',
] as const;
